import { useState } from "react";
import { cn } from "../../../lib/cn";

type Props = {
  serviceName: string;
  dateTime: string;
  staffName: string;
  staffPhotoUrl?: string;
  staffPhotoAlt?: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  onNameChange: (v: string) => void;
  onPhoneChange: (v: string) => void;
  onEmailChange: (v: string) => void;
  nameRequired?: boolean;
  nameDisabled?: boolean;
  phoneDisabled?: boolean;
  onConfirm: () => void;
  isPending: boolean;
  error: string;
  policy?: string;
  highlights?: string[];
  formStyle?: {
    inputVariant?: "outline" | "filled";
    radius?: "sm" | "md" | "lg";
  };
};

export function StepContact({
  serviceName,
  dateTime,
  staffName,
  staffPhotoUrl,
  staffPhotoAlt,
  customerName,
  customerPhone,
  customerEmail,
  onNameChange,
  onPhoneChange,
  onEmailChange,
  nameRequired = true,
  nameDisabled = false,
  phoneDisabled = false,
  onConfirm,
  isPending,
  error,
  policy,
  highlights,
  formStyle,
}: Props) {
  const [policyOpen, setPolicyOpen] = useState(false);
  const formattedDate = dateTime
    ? new Date(dateTime).toLocaleDateString("en", {
        weekday: "long",
        month: "long",
        day: "numeric",
      })
    : "";
  const formattedTime = dateTime
    ? new Date(dateTime).toLocaleTimeString("en", { hour: "numeric", minute: "2-digit" })
    : "";
  const inputVariant = formStyle?.inputVariant || "outline";
  const radius = formStyle?.radius || "md";
  const radiusClass = radius === "sm" ? "rounded-lg" : radius === "lg" ? "rounded-2xl" : "rounded-xl";
  const inputClass = cn(
    "w-full border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent",
    radiusClass,
    inputVariant === "filled" ? "bg-slate-100 border-transparent" : "bg-white border-slate-200"
  );

  return (
    <div className="step-enter">
      <h2 className="text-lg font-semibold text-slate-800 mb-4">Confirm your booking</h2>

      {/* Summary card */}
      <div className="rounded-xl border-l-4 border-teal-600 bg-teal-50/50 p-4 mb-6">
        {staffPhotoUrl ? (
          <img
            src={staffPhotoUrl}
            alt={staffPhotoAlt || staffName || "Specialist photo"}
            className="mb-3 h-16 w-16 rounded-full border border-teal-200 object-cover"
          />
        ) : null}
        <p className="font-semibold text-slate-800">{serviceName}</p>
        <p className="text-sm text-slate-600 mt-1">
          {formattedDate} at {formattedTime}
        </p>
        {staffName && <p className="text-sm text-slate-600">with {staffName}</p>}
      </div>

      {/* Form */}
      <div className="grid gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Name {nameRequired && <span className="text-red-500">*</span>}
          </label>
          <input
            type="text"
            value={customerName}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="Your full name"
            disabled={nameDisabled}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
          <input
            type="tel"
            value={customerPhone}
            onChange={(e) => onPhoneChange(e.target.value)}
            placeholder="+1 (555) 000-0000"
            disabled={phoneDisabled}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Email <span className="text-slate-400 font-normal">(optional)</span>
          </label>
          <input
            type="email"
            value={customerEmail}
            onChange={(e) => onEmailChange(e.target.value)}
            placeholder="you@example.com"
            className={inputClass}
          />
        </div>
      </div>

      {/* Policy */}
      {(policy || (highlights && highlights.length > 0)) && (
        <div className="mt-6">
          <button
            onClick={() => setPolicyOpen(!policyOpen)}
            className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-800"
          >
            <svg className="w-4 h-4 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Before you arrive
            <svg
              className={cn("w-4 h-4 transition-transform", policyOpen && "rotate-180")}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {policyOpen && (
            <div className="mt-2 rounded-xl bg-amber-50 border border-amber-100 p-4 text-sm text-slate-700">
              {policy && <p>{policy}</p>}
              {highlights && highlights.length > 0 && (
                <ul className="mt-2 grid gap-1">
                  {highlights.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="text-amber-500 mt-0.5">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {/* Confirm button */}
      <button
        onClick={onConfirm}
        disabled={isPending || (nameRequired && !customerName.trim())}
        className={cn(
          "mt-6 w-full py-4 rounded-xl text-lg font-bold text-white transition-all",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          !isPending && "hover:shadow-lg"
        )}
        style={{ background: "var(--client-primary)" }}
      >
        {isPending ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Confirming...
          </span>
        ) : (
          "Confirm Booking"
        )}
      </button>

      {error && (
        <div className="mt-3 rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700">
          {error}
        </div>
      )}
    </div>
  );
}
